import moment from 'moment';

import convertToCelsiusResult from '@shared/lib/converter/convertToCelsiusResult';
import TemperatureUnit from '@shared/lib/converter/constants';

const unitPreferencesMixin = {
  computed: {
    dateFormat() {
      const { partnerSettings } = this.$store.state.admin;
      const { DATE_FORMAT = 'MM-DD-YYYY' } = partnerSettings;
      if (DATE_FORMAT === 'MM-DD-YYYY') {
        return 'M/D/YYYY';
      }
      return 'D/M/YYYY';
    },
    dateAndYearFormat() {
      const { partnerSettings } = this.$store.state.admin;
      const { DATE_FORMAT = 'MM-DD-YYYY' } = partnerSettings;
      return DATE_FORMAT;
    },
    tempFormat() {
      const { partnerSettings } = this.$store.state.admin;
      const { TEMP_UNIT = '°F' } = partnerSettings;
      return TEMP_UNIT;
    },
    pressureFormat() {
      const { partnerSettings } = this.$store.state.admin;
      const { PRESSURE_UNIT = 'inH2O' } = partnerSettings;
      return PRESSURE_UNIT;
    },
  },
  methods: {
    date(value) {
      if (value) {
        return moment(value).format(`${this.dateFormat} | h:mmA`);
      }
      return '--';
    },
    dateAndYear(value) {
      if (value) {
        return moment(value).format(this.dateAndYearFormat);
      }
      return '--';
    },
    formattedDate(date) {
      const dateFormat =
        this.dateFormat === 'M/D/YYYY' ? 'M-DD-YYYY | h:mm A' : 'D-MM-YYYY | h:mm A';
      if (date) {
        return moment(date).format(dateFormat);
      }
      return '--';
    },
    dateWithoutHour(value, format) {
      let stdFormat = 'M-DD-YY';
      if (format) {
        stdFormat = format;
      }
      if (value) {
        return moment(value).format(`${stdFormat}`);
      }
      return '--';
    },
    fullDateFormat(value, format) {
      if (value) {
        if (format === 'D/M') {
          return moment(value).format('Do MMMM, YYYY');
        }
        return moment(value).format('MMM Do, YYYY');
      }
      return '--';
    },
    formatDateString(date, format) {
      const parts = date.split(' / ');
      const datePart = parts[0];
      const timePart = parts[1];

      const parsedDate = moment(datePart, 'YYYY-MM-DD');

      if (format === 'D/M') {
        return `${parsedDate.format('DD-MM-YYYY')} / ${timePart}`;
      }
      if (format === 'M/D') {
        return `${parsedDate.format('MM-DD-YYYY')} / ${timePart}`;
      }
      return date;
    },
    getOATemp(c, tempFormat) {
      const { tempAPI, rhAPI } = c;

      let temp = '--';
      let rH = '--';

      const format = tempFormat || '°F';

      if (tempAPI !== null && tempAPI !== undefined) {
        if (format !== '°F') {
          const convertToCelsius = Math.round((tempAPI - 32) * (5 / 9));
          temp = `${convertToCelsius}`;
        } else {
          temp = `${tempAPI}`;
        }
      }
      if (rhAPI) {
        rH = rhAPI;
      }

      return `${Number(temp).toFixed(0)}${format}/${rH}`;
    },
    dateV2(date, format) {
      if (date) {
        if (format === 'D/M') {
          return moment(date).format('D/MM/YYYY');
        }
        return moment(date).format('M/DD/YYYY');
      }
      return '--';
    },
    determineDeltaTFormat(c) {
      const { supplyAir, returnAir, acMode, deltaT } = c;

      if (!deltaT || !supplyAir || !returnAir) {
        return '--';
      }
      let dT = deltaT;
      if (this.tempFormat === '°C') {
        dT = convertToCelsiusResult(deltaT);
      }

      dT = Math.abs(dT).toFixed(0);

      if (acMode === 'Cool') {
        return `-${dT}${this.tempFormat}`;
      }

      if (acMode === 'Heat') {
        return `+${dT}${this.tempFormat}`;
      }

      return `${dT}${this.tempFormat}`;
    },
    convertDeltaPFormat(c) {
      const { pressureConversion } = this;
      const { pressureChange } = c;

      if (!pressureChange) {
        return '--';
      }
      const { onChange } = pressureChange;
      if (this.pressureFormat === 'Pa') {
        const conversionFactor = 249.082;
        const convertedValue = Number(Math.abs(onChange / pressureConversion).toFixed(2));
        return `${Math.round(convertedValue * conversionFactor)}Pa`;
      }
      return `${Math.abs(onChange / pressureConversion).toFixed(2)}inH2O`;
    },
    convertToCelsius(value) {
      if (this.tempFormat !== '°F') {
        return Math.round((value - 32) * (5 / 9));
      }
      return Math.round(value);
    },
    replaceFahrenheitWithCelsius(text) {
      const regex = /(-?\d+\.?\d*)\s?°F/;
      const match = text.match(regex);

      if (match && this.tempFormat !== '°F') {
        const tempFahrenheit = parseFloat(match[1]);
        const tempCelsiusFormatted = `${convertToCelsiusResult(tempFahrenheit)}°C`;
        return text.replace(regex, tempCelsiusFormatted);
      }
      return text;
    },
    fmtT(temp) {
      return `${Math.round(temp)}${this.tempFormat}`;
    },
    supRetFormat(cycle) {
      const { tempFormat } = this;

      const useFahrenheit = tempFormat === TemperatureUnit.FAHRENHEIT;
      let supplyTemp = null;
      let returnTemp = null;

      if (cycle?.dtPayload) {
        const { dtPayload: { sup, ret } = {} } = cycle;
        supplyTemp = Math.round(useFahrenheit ? sup : this.convertToCelsius(sup));
        returnTemp = Math.round(useFahrenheit ? ret : this.convertToCelsius(ret));
      } else {
        const { supplyAir, returnAir, acMode } = cycle;

        let supplyAirTemp = supplyAir.maxTemp;
        let returnAirTemp = returnAir.maxTemp;
        if (acMode === 'Cool') {
          supplyAirTemp = supplyAir.minTemp;
          returnAirTemp = returnAir.minTemp;
        } else if (acMode === 'Fan') {
          supplyAirTemp = supplyAir.avgTemp;
          returnAirTemp = returnAir.avgTemp;
        }

        supplyTemp = Math.round(
          useFahrenheit ? supplyAirTemp : this.convertToCelsius(supplyAirTemp),
        );
        returnTemp = Math.round(
          useFahrenheit ? returnAirTemp : this.convertToCelsius(returnAirTemp),
        );
      }
      return `${this.fmtT(supplyTemp)}/${this.fmtT(returnTemp)}`;
    },
    calculateTempUnit(t, tempFormat = this.tempFormat) {
      if (t) {
        const temp = tempFormat === '°C' ? this.convertToCelsius(t) : t;
        return `${Math.round(temp)}${tempFormat}`;
      }
      return '--';
    },
    deltaTInterval(d, acMode) {
      if (d.deltaTs[0]?.deltaT) {
        const temp =
          this.tempFormat === '°C'
            ? convertToCelsiusResult(d.deltaTs[0].deltaT)
            : d.deltaTs[0].deltaT;
        const deltaT = Math.abs(Math.round(temp));
        if (acMode === 'Cool') {
          return `-${deltaT}${this.tempFormat}`;
        }

        if (acMode === 'Heat') {
          return `+${deltaT}${this.tempFormat}`;
        }

        return `${deltaT}${this.tempFormat}`;
      }
      return '--';
    },
  },
};

export default unitPreferencesMixin;
